// VoiceFabric Phase-4 — per-rule visual flow canvas at
// /admin/voicefabric/flow/:domainId/:ruleId.
//
// Renders the rule as: Trigger ─▶ [Condition ─▶ Action] × N branches.
// Auto-laid-out with dagre on first open; user can drag to fine-tune.
// Selecting a Condition or Action node opens the BranchDrawer for inline edit.
// "Save Flow" persists the updated branches list back to the rule via
// PUT /api/admin/domains/:domainId/voicefabric/rules/:ruleId.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MiniMap, ReactFlowProvider,
  applyNodeChanges, applyEdgeChanges, addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, LayoutTemplate, Loader2 } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import AdminLayout from '../../AdminLayout';
import { NODE_TYPES } from './FlowNodes';
import { layoutWithDagre } from './dagreLayout';
import { BranchDrawer } from './BranchDrawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const newBranchId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
  `b-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ── graph builders ──────────────────────────────────────────────────────────

const buildGraph = (rule, branches, integrations, connections) => {
  if (!rule) return { nodes: [], edges: [] };

  const triggerNode = {
    id: `trigger-${rule.id}`,
    type: 'trigger',
    position: { x: 0, y: 0 },
    data: {
      label: rule.name,
      prompt: rule.trigger_prompt,
      ruleId: rule.id,
    },
  };

  const nodes = [triggerNode];
  const edges = [];

  branches.forEach((b, idx) => {
    const conditionId = `cond-${b.id}`;
    const actionId = `act-${b.id}`;
    const integrationSpec = integrations.find(i => i.id === b.action_type);
    const connection = (b.action_config?.connection_id)
      ? connections.find(c => c.id === b.action_config.connection_id)
      : null;

    nodes.push({
      id: conditionId,
      type: 'condition',
      position: { x: 0, y: 0 },
      data: {
        branchId: b.id,
        label: b.label || `Branch ${idx + 1}`,
        when: b.when || {},
        when_expr: b.when_expr || '',
      },
    });
    nodes.push({
      id: actionId,
      type: 'action',
      position: { x: 0, y: 0 },
      data: {
        branchId: b.id,
        action_type: b.action_type || 'log',
        action_label: integrationSpec?.label || b.action_type || 'log',
        connection_name: connection?.name || '',
      },
    });
    edges.push({
      id: `e-${triggerNode.id}-${conditionId}`,
      source: triggerNode.id, target: conditionId,
      style: { stroke: '#0d9488', strokeWidth: 1.5 },
      animated: true,
    });
    edges.push({
      id: `e-${conditionId}-${actionId}`,
      source: conditionId, target: actionId,
      style: { stroke: '#a78bfa', strokeWidth: 1.5 },
    });
  });

  return { nodes, edges };
};

// ── main canvas ─────────────────────────────────────────────────────────────

const FlowCanvasInner = () => {
  const { domainId, ruleId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rule, setRule] = useState(null);
  const [allRules, setAllRules] = useState([]);
  const [branches, setBranches] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [dirty, setDirty] = useState(false);

  // ── initial fetch ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [rulesR, intR, connR] = await Promise.all([
          axios.get(`${API}/admin/domains/${domainId}/voicefabric/rules`, { headers }),
          axios.get(`${API}/admin/voicefabric/integrations`, { headers }),
          axios.get(`${API}/admin/domains/${domainId}/voicefabric/connections`, { headers }),
        ]);
        if (cancelled) return;
        const r = (rulesR.data.rules || []).find(x => x.id === ruleId);
        if (!r) {
          toast.error('Rule not found');
          navigate('/admin/voicefabric');
          return;
        }
        setAllRules(rulesR.data.rules || []);
        const initialBranches = (r.branches || []).map(b => ({
          ...b,
          id: b.id || newBranchId(),
        }));
        // If rule has zero branches, seed one default branch from the rule's
        // top-level action so the canvas isn't empty.
        if (initialBranches.length === 0) {
          initialBranches.push({
            id: newBranchId(),
            label: 'Default',
            when: {},
            when_expr: '',
            action_type: r.action_type || 'log',
            action_config: r.action_config || {},
            payload_template: '',
            enabled: true,
          });
        }
        setRule(r);
        setBranches(initialBranches);
        setIntegrations(intR.data.integrations || intR.data || []);
        setConnections(connR.data.connections || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load flow');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [domainId, ruleId, headers, navigate]);

  // ── rebuild graph + auto-layout when branches/connections change ─────────
  useEffect(() => {
    if (!rule) return;
    const { nodes: rawNodes, edges: rawEdges } = buildGraph(rule, branches, integrations, connections);
    setNodes(layoutWithDagre(rawNodes, rawEdges));
    setEdges(rawEdges);
  }, [rule, branches, integrations, connections]);

  // ── ReactFlow handlers ────────────────────────────────────────────────────
  const onNodesChange = useCallback(
    changes => setNodes(ns => applyNodeChanges(changes, ns)),
    []
  );
  const onEdgesChange = useCallback(
    changes => setEdges(es => applyEdgeChanges(changes, es)),
    []
  );
  const onConnect = useCallback(
    params => setEdges(es => addEdge({ ...params, animated: true }, es)),
    []
  );

  const onNodeClick = (_e, node) => {
    if (node.type === 'condition' || node.type === 'action') {
      setSelectedBranchId(node.data.branchId);
    } else {
      setSelectedBranchId(null);
    }
  };

  // ── branch CRUD (in memory, persisted on Save) ───────────────────────────
  const updateBranch = useCallback((updated) => {
    setBranches(bs => bs.map(b => (b.id === updated.id ? updated : b)));
    setDirty(true);
  }, []);

  const deleteBranch = useCallback((branchId) => {
    setBranches(bs => bs.filter(b => b.id !== branchId));
    setSelectedBranchId(null);
    setDirty(true);
  }, []);

  const addBranch = () => {
    setBranches(bs => [
      ...bs,
      {
        id: newBranchId(),
        label: `Branch ${bs.length + 1}`,
        when: {},
        when_expr: '',
        action_type: 'log',
        action_config: {},
        payload_template: '',
        enabled: true,
      },
    ]);
    setDirty(true);
  };

  const reLayout = () => {
    if (!rule) return;
    const { nodes: rawNodes, edges: rawEdges } = buildGraph(rule, branches, integrations, connections);
    setNodes(layoutWithDagre(rawNodes, rawEdges));
    setEdges(rawEdges);
  };

  const saveFlow = async () => {
    setSaving(true);
    try {
      // Strip transient fields before PUT — backend re-assigns IDs only when missing
      const clean = branches.map(b => ({
        id: b.id,
        label: b.label || '',
        when: b.when || {},
        when_expr: b.when_expr || '',
        action_type: b.action_type || 'log',
        action_config: b.action_config || {},
        payload_template: b.payload_template || '',
        enabled: b.enabled !== false,
        next_rule_id: b.next_rule_id || '',
      }));
      await axios.put(
        `${API}/admin/domains/${domainId}/voicefabric/rules/${ruleId}`,
        { branches: clean },
        { headers }
      );
      toast.success('Flow saved');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── action options for the drawer ────────────────────────────────────────
  const actionOptions = useMemo(() => {
    const base = [
      { id: 'log', label: 'Log Only', group: 'Built-in', status: 'ready' },
      { id: 'webhook', label: 'Webhook POST', group: 'Built-in', status: 'ready' },
    ];
    const fromCatalog = (integrations || []).map(i => ({
      id: i.id,
      label: i.label || i.id,
      group: i.category || 'Integrations',
      status: i.status || 'ready',
    }));
    return [...base, ...fromCatalog];
  }, [integrations]);

  const selectedBranch = branches.find(b => b.id === selectedBranchId) || null;

  // Stable references — prevents ReactFlow from re-rendering children on every parent update.
  const reactFlowProOptions = useMemo(() => ({ hideAttribution: true }), []);
  const miniMapStyle = useMemo(
    () => ({ background: '#0A0F1C', border: '1px solid rgba(255,255,255,0.05)' }),
    [],
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 text-white/30 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="-mx-6 -my-6 h-[calc(100vh-64px)] flex flex-col bg-[#070B14]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/5 bg-[#0A0F1C]">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/voicefabric')}
              className="text-white/40 hover:text-white p-1.5 rounded hover:bg-white/5"
              data-testid="flow-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-white text-sm font-semibold">{rule?.name || 'Flow'}</h1>
              <p className="text-white/40 text-xs">
                {branches.length} branch{branches.length === 1 ? '' : 'es'}
                {dirty && <span className="text-amber-300 ml-2">• unsaved</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={addBranch}
              className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
              data-testid="flow-add-branch-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add branch
            </button>
            <button
              onClick={reLayout}
              className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
              data-testid="flow-relayout-btn"
            >
              <LayoutTemplate className="w-3.5 h-3.5" /> Auto-arrange
            </button>
            <button
              onClick={saveFlow}
              disabled={saving || !dirty}
              className="flex items-center gap-1.5 text-white text-xs px-4 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 disabled:bg-white/10 disabled:text-white/30 transition-colors"
              data-testid="flow-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save flow
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={NODE_TYPES}
            fitView
            colorMode="dark"
            proOptions={reactFlowProOptions}
          >
            <Background color="#1f2937" gap={24} />
            <Controls className="!bg-[#0A0F1C] !border-white/10" />
            <MiniMap
              pannable zoomable
              nodeStrokeColor="#0d9488"
              nodeColor="#0E1422"
              maskColor="rgba(0,0,0,0.6)"
              style={miniMapStyle}
            />
          </ReactFlow>

          {selectedBranch && (
            <BranchDrawer
              key={selectedBranch.id}
              branch={selectedBranch}
              onChange={updateBranch}
              onDelete={() => deleteBranch(selectedBranch.id)}
              onClose={() => setSelectedBranchId(null)}
              actionOptions={actionOptions}
              connections={connections}
              token={token}
              rules={allRules}
              currentRuleId={ruleId}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const FlowCanvasPage = () => (
  <ReactFlowProvider>
    <FlowCanvasInner />
  </ReactFlowProvider>
);

export default FlowCanvasPage;
